import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { Wordmark } from "@/components/Logo";
import api from "@/lib/api";
import { Compass, ExternalLink, Waypoints, BookOpen, Library, Bot, Gavel, ListOrdered } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

const KIND_ICON = { source: ExternalLink, course: BookOpen, term: Library, practice: Bot, case: Gavel };

function Step({ step, index }) {
  const Icon = KIND_ICON[step.kind] || Compass;
  const external = /^https?:\/\//.test(step.to);
  const inner = (
    <div className="flex gap-4 group" data-testid={`path-step-${index}`}>
      <div className="flex flex-col items-center">
        <span className="w-8 h-8 shrink-0 rounded-full border border-primary/40 text-primary flex items-center justify-center font-mono text-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors">{index + 1}</span>
        <span className="w-px flex-1 bg-border mt-1" />
      </div>
      <div className="pb-7">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <span className="font-medium text-foreground group-hover:text-primary transition-colors">{step.title}</span>
          {external && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
        </div>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{step.note}</p>
      </div>
    </div>
  );
  return external
    ? <a href={step.to} target="_blank" rel="noreferrer" className="block">{inner}</a>
    : <Link to={step.to} className="block">{inner}</Link>;
}

export default function Teachings() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [searchParams] = useSearchParams();
  const src = searchParams.get("src");

  useEffect(() => {
    api.get("/teachings").then((r) => {
      setItems(r.data);
      const target = src && r.data.find((t) => t.id === src) ? src : (r.data[0] && r.data[0].id);
      setOpen(target);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    if (src && items.length) {
      const el = document.querySelector(`[data-testid="teaching-${src}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [src, items]);

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-6 py-14">
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Explore · Teacher Playlists</span>
        <h1 className="font-serif text-5xl mt-2 mb-3">Teachings &amp; Sources</h1>
        <p className="text-muted-foreground max-w-2xl mb-4">
          Independent teachers and archives, each turned into a short <strong>guided reading path</strong> that threads
          their ideas through your courses, the dictionary, and case law. They also appear as stars on the{" "}
          <Link to="/map" className="text-primary hover:underline inline-flex items-center gap-1"><Waypoints className="w-3.5 h-3.5" /> Rabbit Hole</Link> map.
        </p>
        <p className="text-xs text-muted-foreground/80 font-mono mb-10">
          Third-party resources on their own sites; some require purchase or membership. <Wordmark /> is not affiliated with them. For educational purposes only.
        </p>

        <div className="space-y-4">
          {items.map((t) => {
            const expanded = open === t.id;
            return (
              <div key={t.id} className="border border-border bg-card" data-testid={`teaching-${t.id}`}>
                <button onClick={() => setOpen(expanded ? null : t.id)} className="w-full text-left p-6 flex items-start gap-4" data-testid={`teaching-toggle-${t.id}`}>
                  <Compass className="w-6 h-6 text-primary shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-serif text-2xl leading-tight">{t.title}</h2>
                      <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground border border-border px-2 py-0.5">{t.kind}</span>
                      <span className="text-xs text-muted-foreground font-mono">{t.author}</span>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed mt-2">{t.blurb}</p>
                  </div>
                  <span className="shrink-0 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-primary">
                    <ListOrdered className="w-4 h-4" /> {expanded ? "Hide path" : "Reading path"}
                  </span>
                </button>

                {expanded && (
                  <div className="px-6 pb-6" data-testid={`teaching-path-${t.id}`}>
                    <div className="border-t border-border pt-6 mb-2 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Guided reading path · {(t.path || []).length} steps</span>
                      <a href={t.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                        Visit {t.title} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="pl-1">
                      {(t.path || []).map((s, i) => <Step key={i} step={s} index={i} />)}
                      <div className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <span className="w-8 h-8 shrink-0 rounded-full bg-primary/15 border border-primary text-primary flex items-center justify-center">★</span>
                        </div>
                        <div className="pt-1 text-sm text-muted-foreground">Path complete — you've threaded {t.title}'s ideas through the Academy.</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
